import { useSelector } from 'react-redux'

import { ApplicationState } from 'redux/Stores/types'

import {
  selectAllowAccessApiKeys,
  selectAllowAccessExportTables,
  selectAllowAccessAnalyses,
  selectAllowEditDashboardGroup,
  selectAllowEmbedDashboard,
  selectAllowAccessMetric,
  selectCanEditDashboard
} from 'redux/selectors/Permissions'

/**
 * Hook for checking if the user has access to export tables
 * @returns boolean
 */
export const useAllowAccessExportTables = () => {
  return useSelector(selectAllowAccessExportTables)
}

/**
 * Hook for checking if the user can manage API keys
 * @returns boolean
 */
export const useAllowAccessApiKeys = () => {
  return useSelector(selectAllowAccessApiKeys)
}

/**
 * Hook for checking if the user has access to analyses (and activities).
 * @returns boolean
 */
export const useAllowAccessAnalyses = () => {
  return useSelector(selectAllowAccessAnalyses)
}

/**
 * Hook for checking if the current user is allowed to embed dashboards.
 * @returns boolean
 */
export const useAllowEmbedDashboard = () => {
  return useSelector(selectAllowEmbedDashboard)
}

/**
 * Hook for checking if the current user can edit a dashboard group.
 * Returns true if the user is an admin, a Homepal user,
 * or the owner of the dashboard group.
 * @param dashboardGroupId - Dashboard group ID
 * @returns boolean
 */
export const useAllowEditDashboardGroup = (
  dashboardGroupId: string | null | undefined
) => {
  return useSelector((state: ApplicationState) =>
    selectAllowEditDashboardGroup(state, dashboardGroupId)
  )
}

/**
 * Hook for checking if the current user can edit a dashboard.
 * Returns true if the user is an admin, a Homepal user,
 * or the owner of the dashboard's group.
 * @param dashboardId - Dashboard ID
 * @returns boolean
 */
export const useAllowEditDashboard = (
  dashboardId: string | null | undefined
) => {
  return useSelector((state: ApplicationState) =>
    selectCanEditDashboard(state, dashboardId)
  )
}

/**
 * Hook for checking if the current user has access to a metric.
 * Returns false if the metric is private and the user has
 * no explicit permission.
 * @param metricId - Metric ID
 * @returns boolean
 */
export const useAllowAccessMetric = (metricId: string | null | undefined) => {
  return useSelector((state: ApplicationState) =>
    selectAllowAccessMetric(state, metricId)
  )
}
